import { getAuthToken, getAuthHeaders } from '@/utils/auth';
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Car, Watch, Gift, MapPin, Heart, Share2, ChevronLeft, ChevronRight, 
  X, ZoomIn, Star, Calendar, Phone, Mail, Shield, Award, Gauge,
  Fuel, Settings, Palette, ArrowLeft, Check, Clock, Gem, Tag
} from 'lucide-react';
import axios from 'axios';
import Nav from '@/components/Nav';
import { toast } from 'sonner';
import { useCurrency } from '@/context/CurrencyContext';
import PriceDisplay from '@/components/PriceDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lightbox Gallery Component
const ImageGallery = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  const defaultImages = {
    cars: [
      'https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?w=1200',
      'https://images.unsplash.com/photo-1580274455191-1c62238fa333?w=1200',
    ],
    watches: [
      'https://images.unsplash.com/photo-1587836374828-4dbafa94cf0e?w=1200',
      'https://images.unsplash.com/photo-1523170335258-f5ed11844a49?w=1200',
    ],
    accessories: [
      'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=1200',
    ]
  };

  const galleryImages = images?.length > 0 ? images : defaultImages.cars;

  const nextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
  }, [galleryImages.length]);

  const prevImage = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, [galleryImages.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, nextImage, prevImage]);

  return (
    <>
      <div className="relative group">
        <div 
          className="aspect-[4/3] overflow-hidden rounded-2xl cursor-pointer relative"
          onClick={() => setLightboxOpen(true)}
        >
          <img
            src={galleryImages[currentIndex]}
            alt={`${title} - Image ${currentIndex + 1}`}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
            <ZoomIn className="w-4 h-4 text-white" />
            <span className="text-white text-sm">Click to enlarge</span>
          </div>
          <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-lg">
            <span className="text-white text-sm">{currentIndex + 1} / {galleryImages.length}</span>
          </div>
        </div>

        {galleryImages.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {galleryImages.length > 1 && (
        <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
          {galleryImages.map((img, idx) => (
            <button
              key={`md-${idx}`}
              onClick={() => setCurrentIndex(idx)}
              className={`flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex ? 'border-gold ring-2 ring-gold/30' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="absolute top-6 left-6 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg z-10">
              <span className="text-white">{currentIndex + 1} / {galleryImages.length}</span>
            </div>

            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={`max-w-[90vw] max-h-[85vh] ${isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
              onClick={(e) => { e.stopPropagation(); setIsZoomed(!isZoomed); }}
            >
              <img
                src={galleryImages[currentIndex]}
                alt={`${title} - Fullscreen`}
                className={`max-w-full max-h-[85vh] object-contain transition-transform duration-300 ${
                  isZoomed ? 'scale-150' : 'scale-100'
                }`}
              />
            </motion.div>

            {galleryImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 bg-black/50 backdrop-blur-sm p-2 rounded-xl">
              {galleryImages.map((img, idx) => (
                <button
                  key={`md-${idx}`}
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                  className={`w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === currentIndex ? 'border-gold' : 'border-transparent opacity-50 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Inquiry Modal
const InquiryModal = ({ item, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        item_id: item.id,
        item_type: item.category,
        status: 'new'
      });
      toast.success('Inquiry submitted! Our team will contact you soon.');
      onClose();
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-obsidian border border-white/20 rounded-2xl max-w-lg w-full p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-playfair text-2xl font-semibold">Inquire About This Item</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-text-secondary mb-2 block">Your Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-gold outline-none"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="text-sm text-text-secondary mb-2 block">Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-gold outline-none"
              placeholder="john@example.com"
            />
          </div>
          <div>
            <label className="text-sm text-text-secondary mb-2 block">Phone *</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-gold outline-none"
              placeholder="+971 50 123 4567"
            />
          </div>
          <div>
            <label className="text-sm text-text-secondary mb-2 block">Message</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-gold outline-none h-24 resize-none"
              placeholder="I'm interested in this item..."
            />
          </div>
        </div>

        <div className="flex gap-4 mt-8">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/20">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="flex-1 bg-gold text-black hover:bg-gold-light"
          >
            {loading ? 'Submitting...' : 'Send Inquiry'}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Car Detail View
const CarDetailView = ({ item, onInquire, onSave, isSaved }) => {
  const { formatPrice } = useCurrency();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
      <div className="lg:col-span-2 space-y-8">
        <ImageGallery images={item.images} title={item.title} />
        
        <div>
          <div className="flex items-center gap-3 mb-3">
            {item.luxury_tier && (
              <span className="bg-gold/20 text-gold px-3 py-1 rounded-full text-sm font-medium">
                {item.luxury_tier} Tier
              </span>
            )}
            {item.condition && (
              <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-sm">
                {item.condition}
              </span>
            )}
            {item.featured && (
              <span className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full text-sm">
                Featured
              </span>
            )}
          </div>
          <h1 className="font-playfair text-4xl font-semibold mb-2">{item.title || item.model}</h1>
          <div className="flex items-center gap-2 text-text-secondary text-lg">
            <Car className="w-5 h-5 text-gold" />
            <span>{item.brand}</span>
            {item.year && <span>• {item.year}</span>}
          </div>
        </div>

        {/* Car Specifications */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card p-6 text-center">
            <Calendar className="w-8 h-8 text-gold mx-auto mb-2" />
            <div className="text-2xl font-bold">{item.year || 'N/A'}</div>
            <div className="text-text-secondary text-sm">Year</div>
          </div>
          <div className="glass-card p-6 text-center">
            <Gauge className="w-8 h-8 text-gold mx-auto mb-2" />
            <div className="text-2xl font-bold">{item.mileage?.toLocaleString() || '0'}</div>
            <div className="text-text-secondary text-sm">Mileage (km)</div>
          </div>
          <div className="glass-card p-6 text-center">
            <Palette className="w-8 h-8 text-gold mx-auto mb-2" />
            <div className="text-2xl font-bold capitalize">{item.color || 'Various'}</div>
            <div className="text-text-secondary text-sm">Color</div>
          </div>
          <div className="glass-card p-6 text-center">
            <Shield className="w-8 h-8 text-gold mx-auto mb-2" />
            <div className="text-2xl font-bold">{item.condition || 'New'}</div>
            <div className="text-text-secondary text-sm">Condition</div>
          </div>
        </div>

        <div className="glass-card p-8">
          <h2 className="font-playfair text-2xl font-semibold mb-4">Description</h2>
          <p className="text-text-secondary leading-relaxed">
            {item.description || `Experience the pinnacle of automotive excellence with this ${item.brand} ${item.model}. This stunning vehicle combines breathtaking performance with unparalleled luxury, making every drive an unforgettable experience.`}
          </p>
        </div>

        <div className="glass-card p-8">
          <h2 className="font-playfair text-2xl font-semibold mb-6">Vehicle Details</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <div className="text-text-secondary text-sm mb-1">Brand</div>
              <div className="font-semibold">{item.brand}</div>
            </div>
            <div>
              <div className="text-text-secondary text-sm mb-1">Model</div>
              <div className="font-semibold">{item.model}</div>
            </div>
            <div>
              <div className="text-text-secondary text-sm mb-1">Year</div>
              <div className="font-semibold">{item.year || 'N/A'}</div>
            </div>
            <div>
              <div className="text-text-secondary text-sm mb-1">Condition</div>
              <div className="font-semibold">{item.condition || 'New'}</div>
            </div>
            <div>
              <div className="text-text-secondary text-sm mb-1">Location</div>
              <div className="font-semibold">{item.location || 'Dubai'}</div>
            </div>
            <div>
              <div className="text-text-secondary text-sm mb-1">Status</div>
              <div className="font-semibold capitalize">{item.status || 'Available'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-1">
        <div className="sticky top-24 space-y-6">
          <div className="glass-card p-8 border border-gold/30">
            <div className="text-text-secondary mb-2">Sale Price</div>
            <div className="font-playfair text-4xl font-bold text-gold mb-2">
              <PriceDisplay amount={item.price} compact={false} className="text-4xl font-bold text-gold" />
            </div>
            {item.price_usd && (
              <div className="text-text-secondary mb-4">
                ≈ {formatPrice(item.price_usd, { showDual: false })}
              </div>
            )}
            
            {/* Rental Price Section */}
            {item.available_for_rent && item.rental_price_daily && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
                <div className="text-emerald-400 text-sm font-semibold mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Rental Available
                </div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-text-secondary text-sm">Daily Rate</span>
                  <span className="text-emerald-400 font-bold">{formatPrice(item.rental_price_daily)}</span>
                </div>
                {item.rental_price_weekly && (
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-text-secondary text-sm">Weekly Rate</span>
                    <span className="text-emerald-400 font-bold">{formatPrice(item.rental_price_weekly)}</span>
                  </div>
                )}
                {item.rental_price_monthly && (
                  <div className="flex justify-between items-center">
                    <span className="text-text-secondary text-sm">Monthly Rate</span>
                    <span className="text-emerald-400 font-bold">{formatPrice(item.rental_price_monthly)}</span>
                  </div>
                )}
              </div>
            )}
            
            <Button 
              onClick={onInquire}
              className="w-full bg-gold text-black hover:bg-gold-light py-6 text-lg mb-4"
              data-testid="inquire-btn"
            >
              <Phone className="mr-2 w-5 h-5" /> Inquire Now
            </Button>
            
            <Button 
              variant="outline" 
              onClick={onSave}
              className={`w-full py-6 ${isSaved ? 'border-red-500 text-red-500' : 'border-white/20 hover:border-gold'}`}
            >
              <Heart className={`mr-2 w-5 h-5 ${isSaved ? 'fill-current' : ''}`} /> 
              {isSaved ? 'Saved' : 'Save to Wishlist'}
            </Button>
          </div>

          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4">Why Buy From Us</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Check className="w-5 h-5 text-emerald-400" />
                <span>Certified Pre-Owned</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Check className="w-5 h-5 text-emerald-400" />
                <span>Full Service History</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Check className="w-5 h-5 text-emerald-400" />
                <span>Extended Warranty Available</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Check className="w-5 h-5 text-emerald-400" />
                <span>Financing Options</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Watch Detail View
const WatchDetailView = ({ item, onInquire, onSave, isSaved }) => {
  const { formatPrice } = useCurrency();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
      <div className="lg:col-span-2 space-y-8">
        <ImageGallery images={item.images} title={item.title} />
        
        <div>
          <div className="flex items-center gap-3 mb-3">
            {item.luxury_tier && (
              <span className="bg-gold/20 text-gold px-3 py-1 rounded-full text-sm font-medium">
                {item.luxury_tier} Tier
              </span>
            )}
            {item.condition && (
              <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-sm">
                {item.condition}
              </span>
            )}
          </div>
          <h1 className="font-playfair text-4xl font-semibold mb-2">{item.title || item.model}</h1>
          <div className="flex items-center gap-2 text-text-secondary text-lg">
            <Watch className="w-5 h-5 text-gold" />
            <span>{item.brand}</span>
            {item.year && <span>• {item.year}</span>}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card p-6 text-center">
            <Gem className="w-8 h-8 text-gold mx-auto mb-2" />
            <div className="text-2xl font-bold">{item.brand}</div>
            <div className="text-text-secondary text-sm">Brand</div>
          </div>
          <div className="glass-card p-6 text-center">
            <Clock className="w-8 h-8 text-gold mx-auto mb-2" />
            <div className="text-2xl font-bold">{item.year || 'N/A'}</div>
            <div className="text-text-secondary text-sm">Year</div>
          </div>
          <div className="glass-card p-6 text-center">
            <Award className="w-8 h-8 text-gold mx-auto mb-2" />
            <div className="text-2xl font-bold">{item.luxury_tier || 'Premium'}</div>
            <div className="text-text-secondary text-sm">Tier</div>
          </div>
          <div className="glass-card p-6 text-center">
            <Shield className="w-8 h-8 text-gold mx-auto mb-2" />
            <div className="text-2xl font-bold">{item.condition || 'New'}</div>
            <div className="text-text-secondary text-sm">Condition</div>
          </div>
        </div>

        <div className="glass-card p-8">
          <h2 className="font-playfair text-2xl font-semibold mb-4">Description</h2>
          <p className="text-text-secondary leading-relaxed">
            {item.description || `This exquisite ${item.brand} timepiece represents the pinnacle of horological craftsmanship. A true collector's item that combines timeless elegance with precision engineering.`}
          </p>
        </div>

        <div className="glass-card p-8">
          <h2 className="font-playfair text-2xl font-semibold mb-6">Specifications</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <div className="text-text-secondary text-sm mb-1">Brand</div>
              <div className="font-semibold">{item.brand}</div>
            </div>
            <div>
              <div className="text-text-secondary text-sm mb-1">Model</div>
              <div className="font-semibold">{item.model}</div>
            </div>
            <div>
              <div className="text-text-secondary text-sm mb-1">Year</div>
              <div className="font-semibold">{item.year || 'N/A'}</div>
            </div>
            <div>
              <div className="text-text-secondary text-sm mb-1">Condition</div>
              <div className="font-semibold">{item.condition || 'New'}</div>
            </div>
            <div>
              <div className="text-text-secondary text-sm mb-1">Location</div>
              <div className="font-semibold">{item.location || 'Dubai Mall'}</div>
            </div>
            <div>
              <div className="text-text-secondary text-sm mb-1">Availability</div>
              <div className="font-semibold capitalize">{item.status || 'Available'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-1">
        <div className="sticky top-24 space-y-6">
          <div className="glass-card p-8 border border-gold/30">
            <div className="text-text-secondary mb-2">Sale Price</div>
            <div className="font-playfair text-4xl font-bold text-gold mb-2">
              <PriceDisplay amount={item.price} compact={false} className="text-4xl font-bold text-gold" />
            </div>
            {item.price_usd && (
              <div className="text-text-secondary mb-4">
                ≈ {formatPrice(item.price_usd, { showDual: false })}
              </div>
            )}
            
            {/* Rental Price Section */}
            {item.available_for_rent && item.rental_price_daily && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
                <div className="text-emerald-400 text-sm font-semibold mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Rental Available
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary text-sm">Daily Rate</span>
                  <span className="text-emerald-400 font-bold">{formatPrice(item.rental_price_daily)}</span>
                </div>
              </div>
            )}
            
            <Button 
              onClick={onInquire}
              className="w-full bg-gold text-black hover:bg-gold-light py-6 text-lg mb-4"
            >
              <Phone className="mr-2 w-5 h-5" /> Inquire Now
            </Button>
            
            <Button 
              variant="outline" 
              onClick={onSave}
              className={`w-full py-6 ${isSaved ? 'border-red-500 text-red-500' : 'border-white/20 hover:border-gold'}`}
            >
              <Heart className={`mr-2 w-5 h-5 ${isSaved ? 'fill-current' : ''}`} /> 
              {isSaved ? 'Saved' : 'Save to Wishlist'}
            </Button>
          </div>

          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4">Authenticity Guaranteed</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Check className="w-5 h-5 text-emerald-400" />
                <span>Certificate of Authenticity</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Check className="w-5 h-5 text-emerald-400" />
                <span>Original Box & Papers</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Check className="w-5 h-5 text-emerald-400" />
                <span>International Warranty</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Accessory Detail View
const AccessoryDetailView = ({ item, onInquire, onSave, isSaved }) => {
  const { formatPrice } = useCurrency();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
      <div className="lg:col-span-2 space-y-8">
        <ImageGallery images={item.images} title={item.title} />
        
        <div>
          <div className="flex items-center gap-3 mb-3">
            {item.luxury_tier && (
              <span className="bg-gold/20 text-gold px-3 py-1 rounded-full text-sm font-medium">
                {item.luxury_tier} Tier
              </span>
            )}
          </div>
          <h1 className="font-playfair text-4xl font-semibold mb-2">{item.title || item.model}</h1>
          <div className="flex items-center gap-2 text-text-secondary text-lg">
            <Gift className="w-5 h-5 text-gold" />
            <span>{item.brand}</span>
          </div>
        </div>

        <div className="glass-card p-8">
          <h2 className="font-playfair text-2xl font-semibold mb-4">Description</h2>
          <p className="text-text-secondary leading-relaxed">
            {item.description || `This premium ${item.brand} accessory exemplifies luxury craftsmanship. A perfect addition to your collection of fine things.`}
          </p>
        </div>

        <div className="glass-card p-8">
          <h2 className="font-playfair text-2xl font-semibold mb-6">Product Details</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <div>
              <div className="text-text-secondary text-sm mb-1">Brand</div>
              <div className="font-semibold">{item.brand}</div>
            </div>
            <div>
              <div className="text-text-secondary text-sm mb-1">Category</div>
              <div className="font-semibold capitalize">{item.category}</div>
            </div>
            <div>
              <div className="text-text-secondary text-sm mb-1">Location</div>
              <div className="font-semibold">{item.location || 'Dubai'}</div>
            </div>
            <div>
              <div className="text-text-secondary text-sm mb-1">Availability</div>
              <div className="font-semibold capitalize">{item.status || 'Available'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-1">
        <div className="sticky top-24 space-y-6">
          <div className="glass-card p-8 border border-gold/30">
            <div className="text-text-secondary mb-2">Sale Price</div>
            <div className="font-playfair text-4xl font-bold text-gold mb-4">
              <PriceDisplay amount={item.price} compact={false} className="text-4xl font-bold text-gold" />
            </div>
            
            {/* Rental Price Section */}
            {item.available_for_rent && item.rental_price_daily && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
                <div className="text-emerald-400 text-sm font-semibold mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Rental Available
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary text-sm">Daily Rate</span>
                  <span className="text-emerald-400 font-bold">{formatPrice(item.rental_price_daily)}</span>
                </div>
              </div>
            )}
            
            <Button 
              onClick={onInquire}
              className="w-full bg-gold text-black hover:bg-gold-light py-6 text-lg mb-4"
            >
              <Phone className="mr-2 w-5 h-5" /> Inquire Now
            </Button>
            
            <Button 
              variant="outline" 
              onClick={onSave}
              className={`w-full py-6 ${isSaved ? 'border-red-500 text-red-500' : 'border-white/20 hover:border-gold'}`}
            >
              <Heart className={`mr-2 w-5 h-5 ${isSaved ? 'fill-current' : ''}`} /> 
              {isSaved ? 'Saved' : 'Save to Wishlist'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const MarketplaceDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInquiry, setShowInquiry] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const itemId = id || searchParams.get('id');

  useEffect(() => {
    if (itemId) {
      fetchItem();
    }
  }, [itemId]);

  // Check if item is in wishlist on load
  useEffect(() => {
    const checkWishlist = async () => {
      if (item?.id) {
        try {
          const token = getAuthToken();
          if (token) {
            const response = await axios.get(`${API}/wishlist/check/${item.id}`, {
              headers: getAuthHeaders()
            });
            setIsSaved(response.data.in_wishlist);
          }
        } catch (error) {
        }
      }
    };
    checkWishlist();
  }, [item?.id]);

  const fetchItem = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/${itemId}`);
      setItem(response.data);
    } catch (error) {
      toast.error('Item not found');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const handleSaveToWishlist = async () => {
    try {
      if (isSaved) {
        await axios.delete(`${API}/wishlist/${item.id}`);
        setIsSaved(false);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist/add`, {
          item_type: item.category,
          item_id: item.id,
          title: item.title || item.model
        });
        setIsSaved(true);
        toast.success('Added to wishlist!');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please log in to save items');
        navigate('/login');
      } else {
        toast.error('Failed to update wishlist');
      }
    }
  };

  const getCategoryIcon = () => {
    switch (item?.category) {
      case 'cars': return <Car className="w-5 h-5" />;
      case 'watches': return <Watch className="w-5 h-5" />;
      default: return <Gift className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold text-xl">Loading item details...</div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Item not found</h2>
          <Button onClick={() => navigate('/marketplace')} className="bg-gold text-black">
            Back to Marketplace
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-text-primary mobile-pb">
      <Nav showBack activePage="/marketplace" rightContent={
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={handleShare} className="text-text-secondary hover:text-gold">
            <Share2 className="w-5 h-5" />
          </Button>
          <Button 
            variant="ghost" 
            onClick={handleSaveToWishlist} 
            className={isSaved ? 'text-red-500' : 'text-text-secondary hover:text-red-500'}
          >
            <Heart className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
          </Button>
        </div>
      } />

      <div className="max-w-[1440px] mx-auto px-8 py-8">
        {item.category === 'cars' && (
          <CarDetailView item={item} onInquire={() => setShowInquiry(true)} onSave={handleSaveToWishlist} isSaved={isSaved} />
        )}
        {item.category === 'watches' && (
          <WatchDetailView item={item} onInquire={() => setShowInquiry(true)} onSave={handleSaveToWishlist} isSaved={isSaved} />
        )}
        {(item.category === 'accessories' || !['cars', 'watches'].includes(item.category)) && (
          <AccessoryDetailView item={item} onInquire={() => setShowInquiry(true)} onSave={handleSaveToWishlist} isSaved={isSaved} />
        )}
      </div>

      <AnimatePresence>
        {showInquiry && <InquiryModal item={item} onClose={() => setShowInquiry(false)} />}
      </AnimatePresence>
    </div>
  );
};

export default MarketplaceDetailPage;
